import { Check, Close, Delete, Edit } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { getClientCategory, updateClientCategory } from "api/services/client-categories";
import DrawerWrapper from "components/DrawerWrapper";
import Loader from "components/Loader";
import LoadingButton from "components/LoadingButton";
import SafeTextField from "components/SafeTextField";
import { snack } from "components/toast";
import ValidateAccess from "components/ValidateAccess";
import { Permissions } from "data/permissons";
import React from "react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { DialogProps, ResType } from "types";
import { handleError } from "utils/handleError";

interface EditCategoryProps extends DialogProps {
  data: any;
}

function EditCategory({ open, setOpen, data }: EditCategoryProps) {
  const queryClient = useQueryClient();
  const [subCategory, setSubCategory] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [state, setState] = useState<any>({
    name: "",
    subCategories: [],
  });

  const hasDataFromProps = Boolean(data?.name && data?.subCategories);

  React.useEffect(() => {
    if (open && hasDataFromProps) {
      setState({
        name: data.name,
        subCategories: data.subCategories,
      });
    }
  }, [open]);

  const { isLoading: categoryLoading }: ResType = useQuery(["categories", data.id],
    getClientCategory, {
    onSuccess: (response) => {
      if (response && response?.data) {
        setState({
          name: response?.data?.name,
          subCategories: response?.data?.subCategories,
        });
      } else {
        setState({
          name: "",
          subCategories: [],
        });
      }
    },
    onError: (err) => {
      if (!err?.error) {
        snack.error("An error occurred while fetching categories");
      }
    },
    enabled: open && !hasDataFromProps
  });

  const handleChange = (key: string, v: string) => {
    setState({ ...state, [key]: v });
  };

  const { mutate, isLoading } = useMutation(updateClientCategory, {
    onSuccess: () => {
      snack.success("Client Category Updated");
      setOpen(false);
      queryClient.invalidateQueries("client-categories");
    },
    onError: (err: any) => {
      if (err?.response?.response?.data?.message?.[0].includes('subCategories')) {
        snack.error('Subcategory name cannot contain /, |, *, ?, ", <, >');
      } else {
        snack.error(handleError(err));
        setOpen(false);
        setState({
          name: "",
          subCategories: [],
        })
      }

    },
  });

  const addSubCategory = () => {
    if (!subCategory) return snack.error("Enter Subcategory");
    if (/[\/\|\*\?"<>]/.test(subCategory)) return snack.error('Subcategory name cannot contain /, |, *, ?, ", <, >')

    setState({
      ...state,
      subCategories: [...state.subCategories, { name: subCategory }],
    });
    setSubCategory("");
  };

  const deleteSubCategory = (index: any) => {
    let subCategories = [...state.subCategories];
    subCategories.splice(index, 1);
    setState({
      ...state,
      subCategories: subCategories,
    });
  };

  const startEditing = (index: number, currentName: string) => {
    setEditingIndex(index);
    setEditingValue(currentName);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditingValue("");
  };

  const saveEditing = () => {
    if (!editingValue.trim()) return snack.error("Subcategory name cannot be empty");
    if (/[\/\|\*\?"<>]/.test(editingValue)) return snack.error('Subcategory name cannot contain /, |, *, ?, ", <, >');
    const updated = [...state.subCategories];
    updated[editingIndex!] = { ...updated[editingIndex!], name: editingValue.trim() };
    setState({ ...state, subCategories: updated });
    cancelEditing();
  };

  const handleSubmit = () => {
    if (!state.name) return snack.error("Name is required");
    if (subCategory.trim()) return snack.error("Please add or clear the subcategory field before saving");
    if (editingIndex !== null) return snack.error("Please save or cancel the subcategory edit before saving");
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    mutate({
      id: data.id,
      data: state,
    });
  };

  return (categoryLoading ? <Loader /> :
    <DrawerWrapper open={open} setOpen={setOpen} title="Edit Category">
      <SafeTextField
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        fullWidth
        value={state.name}
        disabled={!data?.organization}
        onChange={(e) => handleChange("name", e.target.value)}
        size="small"
        label="Name"
      />
      <>
        <Box display="flex" gap={1} mt={3}>
          <TextField
            variant="outlined"
            fullWidth
            value={subCategory}
            onChange={(e) => setSubCategory(e.target.value)}
            size="small"
            label="Add Subcategory"
          />
          <Button
            onClick={addSubCategory}
            sx={{ minWidth: 80 }}
            variant="outlined"
            color="primary"
          >
            + Add
          </Button>
        </Box>
        <Box my={2}>
          {state.subCategories.map((item, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              gap={1}
            >
              {editingIndex === index ? (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEditing();
                      if (e.key === "Escape") cancelEditing();
                    }}
                  />
                  <IconButton size="small" color="primary" onClick={saveEditing}>
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={cancelEditing}>
                    <Close fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2">
                    {index + 1}. {item.name}
                  </Typography>
                  {(item?.organization || !item?.id) && (
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" onClick={() => startEditing(index, item.name)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <ValidateAccess name={Permissions.DELETE_CLIENT_CATEGORIES}>
                      <IconButton size="small" onClick={() => deleteSubCategory(index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                      </ValidateAccess>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ))}
        </Box>
      </>
      <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isLoading}
          fullWidth
          loadingColor="white"
          title="Update"
          color="secondary"
        />
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This change will reflect across all your existing leads and clients associated with this category. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="secondary" disableElevation>
            Yes, Update
          </Button>
        </DialogActions>
      </Dialog>
    </DrawerWrapper>
  );
}

export default EditCategory;
